import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { nanoid } from 'nanoid';
import { type UseFormReturn, useForm } from 'react-hook-form';

import type { GetPropertyTypesQuery } from '@graphql/__generated__/graphql';
import { GET_PROPERTY_TYPES } from '@modules/_shared/common/queries';
import type { RaAddressType } from '@modules/_shared/components/form/RaAddressInput';
import { useSingleFlight } from '@modules/_shared/hooks/useSingleFlight';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import type { TransformedListingImage } from '@modules/_shared/utils/listingDataTransformers';
import { buildCreateTransactionInput } from '@modules/_shared/utils/transactionHelpers';
import { isTransactionPropertyDetailFieldVisible } from '@modules/_shared/utils/transactionPropertyDetails';
import {
  CREATE_TRANSACTION_WITH_PROPERTY,
  GET_LINKED_LISTING_IDS_FOR_TEAMS,
  GET_PROPERTY_TRANSACTIONS,
  GET_PROPERTY_TRANSACTIONS_LIST_COUNT,
} from '@modules/transactions/transactionQueries';
import { GET_VISIBILITY_TRANSACTIONS } from '@modules/visibility-transactions/visibilityTransactionsQueries';
import { useAnalytics } from '@shared/analytics';
import {
  ANALYTICS_EVENTS,
  type AnalyticsEvent,
} from '@shared/analytics/constants';

import { isSpanishTransactionCountry } from '../transactionCountry';

export type TransactionAddress = RaAddressType;

export type TransactionProperty = {
  composite_type: string;
  property_type_id: string;
  number_of_rooms?: number;
  number_of_bedrooms?: number;
  living_surface?: number;
  land_surface?: number;
  built_surface?: number;
  usable_surface?: number;
};

export type TransactionDetails = {
  purchase_price?: number;
  signature_date?: Date | string | null;
  seller_broker_id?: string | null;
  seller_broker_team_id?: string;
};

export type TransactionPhotos = {
  uploadedImage?: { id: string; url: string };
  listingImage?: TransformedListingImage;
};

export type TransactionFormData = {
  address: TransactionAddress;
  property: TransactionProperty;
  details: TransactionDetails;
  photos: TransactionPhotos;
  selectedListingId?: number;
};

export type StepId =
  | 'address'
  | 'property'
  | 'details'
  | 'photos'
  | 'preview'
  | 'success';

export const TRANSACTION_FORM_STEP_IDS = [
  'address',
  'property',
  'details',
  'photos',
] as const satisfies readonly StepId[];

const STEPS: StepId[] = [...TRANSACTION_FORM_STEP_IDS, 'preview', 'success'];

const STEP_COMPLETION_EVENT_MAP: Partial<Record<StepId, AnalyticsEvent>> = {
  address: ANALYTICS_EVENTS.TRANSACTION_CREATION_ADDRESS_FILLED,
  property: ANALYTICS_EVENTS.TRANSACTION_CREATION_PROPERTY_FILLED,
  details: ANALYTICS_EVENTS.TRANSACTION_CREATION_DETAILS_FILLED,
  photos: ANALYTICS_EVENTS.TRANSACTION_CREATION_PHOTOS_FILLED,
};

const getFieldsForStep = (
  step: StepId,
  formData: TransactionFormData,
  isSpanish: boolean,
  propertyTypes: GetPropertyTypesQuery['property_types'],
): (keyof TransactionFormData | string)[] => {
  switch (step) {
    case 'address':
      return ['address.route'];
    case 'property': {
      const baseFields = [
        'property.composite_type',
        'property.property_type_id',
      ];
      if (
        !formData.property.property_type_id ||
        !formData.property.composite_type
      ) {
        return baseFields;
      }

      const propertyType = propertyTypes.find(
        pt => pt.id === formData.property.property_type_id,
      );
      const candidateFields = isSpanish
        ? (['number_of_bedrooms', 'built_surface'] as const)
        : (['number_of_rooms', 'living_surface'] as const);

      const surfaceFields = ['usable_surface', 'land_surface'] as const;

      for (const field of [...candidateFields, ...surfaceFields]) {
        if (isTransactionPropertyDetailFieldVisible(propertyType, field)) {
          baseFields.push(`property.${field}`);
        }
      }

      return baseFields;
    }
    case 'details':
      return [
        'details.purchase_price',
        'details.signature_date',
        'details.seller_broker_id',
      ];
    default:
      return [];
  }
};

export type UseTransactionStepperOptions = {
  initialData?: TransactionFormData;
  onTransactionCreated?: (transactionId: string) => void;
  skipSuccessStep?: boolean;
  skipSessionRecording?: boolean;
  omitPreviewStep?: boolean;
  initialSellerBrokerId?: string | null;
  initialSellerBrokerTeamId?: string | null;
  countryCode?: string | null;
};

const buildActiveSteps = (
  omitPreview: boolean,
  skipSuccess: boolean,
): StepId[] =>
  STEPS.filter(
    id =>
      !(omitPreview && id === 'preview') && !(skipSuccess && id === 'success'),
  );

type UseTransactionStepperReturn = {
  form: UseFormReturn<TransactionFormData>;
  currentStep: StepId;
  currentStepIndex: number;
  isFirstStep: boolean;
  isLastStep: boolean;
  completedSteps: string[];
  goNext: () => void;
  validateAndGoNext: () => Promise<boolean>;
  goBack: () => void;
  goToStep: (index: number) => void;
  submit: () => void;
  apolloError: ErrorLike | null;
  clearError: () => void;
  nextLabel: string | undefined;
  setNextLabel: (label: string | undefined) => void;
  reset: () => void;
  flowId: string;
  trackListingSelected: (listingId: number) => void;
};

export const useTransactionStepper = (
  options?: UseTransactionStepperOptions,
): UseTransactionStepperReturn => {
  const { me } = useAppData();
  const {
    initialData,
    countryCode,
    initialSellerBrokerId,
    initialSellerBrokerTeamId,
    omitPreviewStep = false,
    onTransactionCreated,
    skipSessionRecording = false,
    skipSuccessStep = false,
  } = options ?? {};
  const activeSteps = useMemo(
    () => buildActiveSteps(omitPreviewStep, skipSuccessStep),
    [omitPreviewStep, skipSuccessStep],
  );

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);
  const [nextLabel, setNextLabel] = useState<string | undefined>();
  const { data: propertyTypesData } = useQuery(GET_PROPERTY_TYPES);
  const { track, posthog } = useAnalytics();

  const [flowId, setFlowId] = useState(() => nanoid());
  // Track if flow completed successfully to prevent abandonment event on unmount
  const isFinishedRef = useRef<boolean>(false);
  const { run: runSubmit } = useSingleFlight();
  const currentStepIndexRef = useRef(currentStepIndex);
  // Store stable references for cleanup function
  const flowIdRef = useRef(flowId);
  const trackRef = useRef(track);

  useEffect(() => {
    currentStepIndexRef.current = currentStepIndex;
  }, [currentStepIndex]);

  useEffect(() => {
    flowIdRef.current = flowId;
    trackRef.current = track;
  }, [flowId, track]);

  // Start session recording for transaction creation flow
  // PostHog manages the session lifecycle automatically:
  // 1. This call starts recording immediately when flow begins
  // 2. Dashboard event trigger (transaction_creation_step_viewed) provides redundancy
  // 3. Session ID is automatically included in events via posthog
  // 4. Recording ends based on PostHog's inactivity timeout (no manual stop needed)
  useEffect(() => {
    if (!skipSessionRecording) {
      posthog.startSessionRecording();
    }
  }, [posthog, skipSessionRecording]);

  const propertyTypes = useMemo(
    () => propertyTypesData?.property_types || [],
    [propertyTypesData],
  );

  const defaultValues = useMemo(
    () => ({
      address: {},
      property: { composite_type: '', property_type_id: '' },
      details: {
        seller_broker_id:
          initialSellerBrokerId ?? (me?.is_broker ? me.id : undefined),
        seller_broker_team_id:
          initialSellerBrokerTeamId ?? me?.default_team?.id ?? undefined,
      },
      photos: {},
      selectedListingId: undefined,
    }),
    [
      me?.id,
      me?.is_broker,
      me?.default_team?.id,
      initialSellerBrokerId,
      initialSellerBrokerTeamId,
    ],
  );

  const form = useForm<TransactionFormData>({
    defaultValues: initialData ?? defaultValues,
    mode: 'onSubmit',
  });

  const getCommonTrackingProperties = useCallback(() => {
    const formData = form?.getValues();
    return {
      flow_id: flowId,
      listing_id: formData?.selectedListingId,
    };
  }, [flowId, form]);

  // Track step views - fires on initial mount and whenever step changes
  useEffect(() => {
    const formData = form?.getValues();
    track(ANALYTICS_EVENTS.TRANSACTION_CREATION_STEP_VIEWED, {
      flow_id: flowId,
      listing_id: formData?.selectedListingId,
      step: activeSteps[currentStepIndex],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStepIndex, flowId, activeSteps]);

  // Track abandonment - only set up once on mount, not on every render
  useEffect(() => {
    return () => {
      if (!isFinishedRef.current) {
        const formData = form?.getValues();
        trackRef.current(ANALYTICS_EVENTS.TRANSACTION_CREATION_CLOSED, {
          flow_id: flowIdRef.current,
          listing_id: formData?.selectedListingId,
          step: activeSteps[currentStepIndexRef.current],
          is_completed: false,
        });
      }
    };
  }, [form, activeSteps]);

  const [createTransaction] = useMutation(CREATE_TRANSACTION_WITH_PROPERTY, {
    onError: error => {
      setErrorLike(error);
      track(ANALYTICS_EVENTS.TRANSACTION_CREATION_FAILED, {
        error: error.message,
        flow_id: flowId,
      });
    },
    awaitRefetchQueries: true,
    refetchQueries: [
      GET_PROPERTY_TRANSACTIONS,
      GET_PROPERTY_TRANSACTIONS_LIST_COUNT,
      GET_LINKED_LISTING_IDS_FOR_TEAMS,
      GET_VISIBILITY_TRANSACTIONS,
    ],
  });

  const currentStep = activeSteps[currentStepIndex];

  useEffect(() => {
    if (currentStep === 'success') {
      isFinishedRef.current = true;
    }
  }, [currentStep]);

  const isFirstStep = currentStepIndex === 0;
  const isLastStep =
    activeSteps[currentStepIndex + 1] === 'success' ||
    currentStepIndex >= activeSteps.length - 1;
  const isSpanish = isSpanishTransactionCountry(
    countryCode,
    me?.tenant.country_code,
  );

  const validateCurrentStep = useCallback(async (): Promise<boolean> => {
    const formData = form.getValues();
    const fields = getFieldsForStep(
      currentStep,
      formData,
      isSpanish,
      propertyTypes,
    );

    if (fields.length === 0) {
      return true;
    }

    const result = await form.trigger(
      fields as Parameters<typeof form.trigger>[0],
    );
    return result;
  }, [form, currentStep, isSpanish, propertyTypes]);

  const goNext = useCallback(() => {
    const i = currentStepIndex;
    if (i >= activeSteps.length - 1) {
      return;
    }

    const currentStepId = activeSteps[i];

    // Capture step completion with relevant metadata
    const completionEvent = STEP_COMPLETION_EVENT_MAP[currentStepId];
    if (completionEvent) {
      const formData = form.getValues();
      const properties: Record<string, any> = getCommonTrackingProperties();

      if (currentStepId === 'property') {
        properties.property_type = propertyTypes.find(
          propertyType =>
            propertyType.id === formData.property.property_type_id,
        )?.main_type;
        properties.property_type_id = formData.property.property_type_id;
      } else if (currentStepId === 'details') {
        properties.sale_price = formData.details.purchase_price;
      } else if (currentStepId === 'photos') {
        properties.has_photo =
          !!formData.photos.uploadedImage || !!formData.photos.listingImage;
      }

      track(completionEvent, properties);
    }

    // Note: Step view tracking is handled by the useEffect with currentStepIndex dependency

    setCompletedSteps(prev =>
      prev.includes(currentStepId) ? prev : [...prev, currentStepId],
    );
    setCurrentStepIndex(i + 1);
    setNextLabel(undefined);
  }, [
    track,
    currentStepIndex,
    form,
    getCommonTrackingProperties,
    propertyTypes,
    activeSteps,
  ]);

  const validateAndGoNext = useCallback(async (): Promise<boolean> => {
    const isValid = await validateCurrentStep();
    if (isValid) {
      goNext();
    } else {
      track(ANALYTICS_EVENTS.TRANSACTION_CREATION_VALIDATION_FAILED, {
        ...getCommonTrackingProperties(),
        step: currentStep,
      });
    }
    return isValid;
  }, [
    validateCurrentStep,
    goNext,
    track,
    currentStep,
    getCommonTrackingProperties,
  ]);

  const trackListingSelected = useCallback(
    (listingId: number) => {
      track(ANALYTICS_EVENTS.TRANSACTION_CREATION_LISTING_SELECTED, {
        listing_id: listingId,
        flow_id: flowId,
      });
    },
    [track, flowId],
  );

  const goToStep = useCallback(
    (index: number) => {
      const target = Math.max(0, Math.min(index, activeSteps.length - 1));
      if (target >= currentStepIndex) {
        return;
      }
      setCompletedSteps(prev =>
        prev.filter(id => {
          const idx = activeSteps.indexOf(id as StepId);
          return idx !== -1 && idx < target;
        }),
      );
      setCurrentStepIndex(target);
      setNextLabel(undefined);
      form.clearErrors();
    },
    [activeSteps, currentStepIndex, form],
  );

  const goBack = useCallback(() => {
    const i = currentStepIndex;
    const newIndex = Math.max(i - 1, 0);

    track(ANALYTICS_EVENTS.TRANSACTION_CREATION_GO_BACK, {
      ...getCommonTrackingProperties(),
      step: activeSteps[i],
    });

    setCompletedSteps(prev => prev.filter(id => id !== activeSteps[newIndex]));
    setCurrentStepIndex(newIndex);
    setNextLabel(undefined);
    form.clearErrors();
  }, [form, track, currentStepIndex, getCommonTrackingProperties, activeSteps]);

  const clearError = useCallback(() => setErrorLike(null), []);

  const reset = useCallback(() => {
    form.reset(defaultValues);
    setCurrentStepIndex(0);
    setCompletedSteps([]);
    setErrorLike(null);
    setNextLabel(undefined);
    setFlowId(nanoid());
  }, [form, defaultValues]);

  const onSubmit = useCallback(
    async (data: TransactionFormData) => {
      await runSubmit(async () => {
        const { data: result } = await createTransaction({
          variables: {
            transaction: buildCreateTransactionInput({
              ...data,
              details: {
                ...data.details,
                seller_broker_id: data.details.seller_broker_id || me?.id,
                seller_broker_team_id:
                  data.details.seller_broker_team_id || me?.default_team?.id,
              },
            }),
          },
        });

        const transactionId = result?.insert_property_transactions_one?.id;
        if (transactionId) {
          isFinishedRef.current = true;
          track(ANALYTICS_EVENTS.TRANSACTION_CREATION_COMPLETED, {
            ...getCommonTrackingProperties(),
            transaction_id: transactionId,
          });
          if (skipSuccessStep && onTransactionCreated) {
            onTransactionCreated(transactionId);
            reset();
            return;
          }
          goNext();
        }
      });
    },
    [
      createTransaction,
      me,
      goNext,
      track,
      getCommonTrackingProperties,
      skipSuccessStep,
      onTransactionCreated,
      reset,
      runSubmit,
    ],
  );

  const submit = form.handleSubmit(onSubmit);

  return {
    form,
    currentStep,
    currentStepIndex,
    isFirstStep,
    isLastStep,
    completedSteps,
    goNext,
    validateAndGoNext,
    goBack,
    goToStep,
    submit,
    apolloError,
    clearError,
    nextLabel,
    setNextLabel,
    reset,
    flowId,
    trackListingSelected,
  };
};
